import { IBomPartPrice } from 'bom/types';
import {
  CorporatePartPackageType,
  CorporatePartStatus,
  CorporatePartTaaStatus,
  CorporateSourceType,
} from 'corporate/enums';
import { PricingType } from 'pricing/enums';

export interface ICorporatePartConfig extends ICorporatePart {
  corporatePricing?: ICorporatePricing;
  isBaseCodePart: number;
  minimumQuantityInQuoteUom?: number;
  orderIncrementInQuoteUom?: number;
  pnbPricingEnabled: boolean;
}

export interface ICorporatePartExtraData {
  externalPrices?: Record<string, IBomPartPrice>;
}

export interface ICorporatePart {
  alternateSalesUom: string;
  alternateUom: string;
  alternateUomSource: CorporateSourceType;
  alternateVendorNumber?: string;
  alternateVendorNumberSource: CorporateSourceType;
  bomPriceDecimals?: number;
  className: string;
  classNameSource: CorporateSourceType;
  companyManufacturerId: number;
  companyManufacturerIdSource: CorporateSourceType;
  companyManufacturerName: string;
  companyManufacturerNameSource: CorporateSourceType;
  companyPartStatus: CorporatePartStatus;
  cost: number;
  costingQuantityDiscounts: boolean;
  externalStandardCost: number;
  externalUom: string;
  externalUomSource: CorporateSourceType;
  floorPrice?: number;
  id: number;
  inventory: IInventory;
  isPnb: boolean;
  listPrice?: number;
  manualFloorPrice?: number;
  manualListPrice?: number;
  manufacturerId: number;
  manufacturerMinimumQuantity?: number;
  manufacturerMinimumQuantityWithUom?: string;
  manufacturerOrderIncrement?: number;
  manufacturerOrderIncrementWithUom: string;
  mfgPartStatus: CorporatePartStatus;
  minimumQuantity?: number;
  minimumQuantityWithUom: string;
  orderIncrement?: number;
  orderIncrementWithUom: string;
  originalListPrice: number;
  originalListPriceUom: string;
  originalManualFloorPrice?: number;
  originalManualFloorPriceUom?: string;
  originalManualListPrice?: number;
  originalManualListPriceUom?: string;
  packageType?: string;
  packageUnits?: number;
  packageUom: string;
  part: string;
  partBaseCode: string;
  pkgUnits: number;
  pkgUnitsWithUom: string;
  pricingGroup1: string;
  pricingGroup2: string;
  pricingGroup3: string;
  pricingGroup4: string;
  pricingGroup5: string;
  pricingGroupDescription1: string;
  pricingGroupDescription2: string;
  pricingGroupDescription3: string;
  pricingGroupDescription4: string;
  pricingGroupDescription5: string;
  pricingQuantityDiscounts: string;
  pricingType: PricingType;
  pricingUom: string;
  purchaseCost: number;
  purchaseCostUom: string;
  quotePriceDecimals: string;
  replacementPart: string;
  salesUom: string;
  taaStatus: CorporatePartTaaStatus;
  uom: string;
  uomId: string;
  uoms?: string[];
  updatedAt?: string;
  vendorNumber: string;
  vendorPartNumber?: string;
}

export interface ICorporatePricing {
  cost?: number;
  costingQuantityDiscounts?: boolean;
  costingStatus?: string;
  discountIncrease?: number;
  floorPrice?: number;
  listPrice?: number;
  manualFloorPrice?: number;
  manualListPrice?: number;
  marginReduction?: number;
  originalManualFloorPrice?: number;
  originalManualFloorPriceUom?: string;
  originalManualListPrice?: number;
  originalManualListPriceUom?: string;
  pricingQuantityDiscounts?: boolean;
  pricingStatus?: string;
  purchaseCost?: number;
  purchasedCost?: number;
  standardCost?: number;
  uom?: string;
}

export enum InventoryStatusAvailabilityTypes {
  AVAILABLE = 'available',
  COMPANY_AVAIALBLE = 'company_available',
  INSUFFICIENT = 'insufficient',
}

export interface IInventory {
  // Quantities are nullable: a null one is stock the BE could not express in the requested UoM,
  // which is unknown - never zero.
  locationQty: number | null;
  status: InventoryStatusAvailabilityTypes;
  totalQty: number | null;
  uom: string;
  userLocationQty: number | null;
}

export class UpdateCorporatePartForm {
  id?: number;
  uomId?: string;
  bomPriceDecimals: number;
  className: string;
  companyManufacturerId: string;
  companyManufacturerName: string;
  companyPartStatus: CorporatePartStatus;
  manufacturerId: number;
  manufacturerMinimumQuantity: number;
  manufacturerOrderIncrement: number;
  mfgPartStatus: CorporatePartStatus;
  minimumQuantity: number;
  orderIncrement: number;
  packageType: CorporatePartPackageType;
  part: string;
  partBaseCode: string;
  pkgUnits: number;
  pricingGroup1: string;
  pricingGroup2: string;
  pricingGroup3: string;
  pricingGroup4: string;
  pricingGroup5: string;
  pricingGroupDescription1: string;
  pricingGroupDescription2: string;
  pricingGroupDescription3: string;
  pricingGroupDescription4: string;
  pricingGroupDescription5: string;
  quotePriceDecimals: string;
  replacementPart: string;
  taaStatus: CorporatePartTaaStatus;
  vendorNumber: string;
}

export interface ICorporatePartRequestPayload {
  accountsReceivableId?: string;
  alternateVersionEnabled?: number;
  part: {
    familyId?: number;
    manufacturerId: number;
    number: string;
    verified?: number;
  };
  salesExternalLocationId?: string;
}

export interface ICorporatePartCostListItem {
  costItems: ICorporatePartCostItem[];
  createdAt: string;
  endDate?: string;
  id: number;
  startDate: string;
  status: string;
  uom: string;
  userFirstName: string;
  userLastName: string;
}

export interface ICorporatePartCostItem {
  cost: number;
  id?: number;
  maxQuantity?: number;
  minQuantity: number;
}

export interface ICorporatePartPriceListItem {
  corporatePartId: number;
  createdAt: string;
  endDate?: string | null;
  id: number;
  priceItems: ICorporatePartPriceItem[];
  startDate: string;
  status: string;
  uom?: string;
  userFirstName: string;
  userLastName: string;
}

export interface ICorporatePartPriceItem {
  corporatePartPriceId?: number;
  discountIncrease?: string | null;
  id?: number;
  marginReduction?: string | null;
  maxQuantity?: string | null;
  minQuantity: string;
}
